import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import AppShell, { ErrorNote } from "@/components/AppShell";
import ReadingForm from "@/components/ReadingForm";
import { api } from "@/lib/api";
import { formatDateTime, useAsync, useVesselPath } from "@/lib/hooks";
import type { ParamKey } from "@/lib/params";
import {
  type ReadingDraft,
  buildUpdatePayload,
  draftFromReading,
  draftHasContent,
} from "@/lib/readingDraft";
import type { Reading ,
  Vessel,
} from "@/types";

export default function EditReading() {
  const vp = useVesselPath();
  // Only to decide which fields are worth asking for; a failure here must not
  // stop you logging a reading, so it degrades to showing everything.
  const vessel = useAsync<Vessel>(() => api.get<Vessel>(""), []);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, error, loading } = useAsync<Reading>(
    () => api.get<Reading>(`/api/readings/${id}`),
    [id],
  );

  const initial = useMemo(() => (data ? draftFromReading(data) : null), [data]);

  async function save(draft: ReadingDraft, shown: ParamKey[]) {
    if (!initial) return;

    const payload = buildUpdatePayload(initial, draft, shown);
    // Never write a record the user didn't change — an empty PUT would still
    // bump updated_at for a form that was only looked at.
    if (Object.keys(payload).length > 0) {
      await api.put<Reading>(`/api/readings/${id}`, payload);
    }
    navigate(vp(`/history/${id}`), { replace: true });
  }

  return (
    <AppShell
      title="Edit reading"
      subtitle={data ? formatDateTime(data.taken_at) : undefined}
      action={
        <Link to={vp(`/history/${id}`)} className="btn btn-quiet shrink-0 px-3 py-2 text-sm">
          Cancel
        </Link>
      }
    >
      {error && <ErrorNote message={error} />}
      {loading && <p className="text-sm text-(--color-ink-muted)">Loading…</p>}

      {data && initial && (
        // Keyed on the record so a different reading re-seeds the fields rather
        // than editing one reading's values into another's.
        <ReadingForm
          vesselType={vessel.data?.vessel_type}
          key={data.id}
          initial={initial}
          submitLabel="Save changes"
          pendingLabel="Saving…"
          errorFallback="Could not save your changes."
          blockedReason={(draft, shown) => {
            // The update endpoint has no "at least one measurement" rule, so
            // the form keeps one: an emptied reading is a delete, not an edit.
            if (!draftHasContent(draft, shown)) {
              return "A reading needs at least one measurement or a note.";
            }
            if (Object.keys(buildUpdatePayload(initial, draft, shown)).length === 0) {
              return "Nothing has changed yet.";
            }
            return null;
          }}
          onSubmit={save}
        />
      )}
    </AppShell>
  );
}
